import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { catchError, tap, throwError } from "rxjs";
import { getSafeExternalUrl } from "src/app/common/functions/redirect.functions";
import { hasStoredSession } from "src/app/common/functions/storage.functions";
import { API } from "src/app/common/models/api.enum";
import { CommonSignalStore } from "src/app/common/signal-stores/common-store/common.store";
import { LogoutStore } from "src/app/common/signal-stores/logout/logout.store";
import { RoutesConstant } from "src/app/constant/routes-constant";
import { AlertService } from "src/app/services/alert.service";

// SSO users are sent back to their portal when the backend provides a
// redirection_url; everyone else goes to the login page.
function performLogout(
  commonStore: InstanceType<typeof CommonSignalStore>,
  logoutStore: InstanceType<typeof LogoutStore>,
  modalService: NgbModal,
  router: Router,
  redirectionUrl?: string,
): void {
  // Read before reset(), which clears the SSO flag
  const ssoRedirectUrl = commonStore.isSso()
    ? getSafeExternalUrl(redirectionUrl)
    : null;

  commonStore.reset();
  logoutStore.clearStore();
  localStorage.clear();
  sessionStorage.clear();
  modalService.dismissAll();

  if (ssoRedirectUrl) {
    window.location.href = ssoRedirectUrl;
    return;
  }
  router.navigate([RoutesConstant.Login]);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const commonStore = inject(CommonSignalStore);
  const router = inject(Router);
  const logoutStore = inject(LogoutStore);
  const alertService = inject(AlertService);
  const modalService = inject(NgbModal);

  // Skip these api as this api needed temporary tokens only
  const skipAuth =
    req.url.includes("converse/twofa") ||
    req.url.includes("converse/session_exist") ||
    req.url.includes("converse/session_logout") ||
    req.url.includes("converse/org_login") ||
    req.url.includes("converse/customer/forgotpassword") ||
    req.url.includes("btjsocket/api/getbotdetails") ||
    req.url.includes("public/encryptMsg") ||
    req.url.includes("btjsocket/api/botSuggestions") ||
    req.url.includes("api.ipify.org");

  if (skipAuth) {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Note: unlike the other logout call sites below, this path does
          // not clear logoutStore/localStorage/sessionStorage. Preserved
          // as-is (not folded into performLogout) since that would be a
          // behavior change, not just deduplication — flagging for review:
          // an invalid temp token here plausibly warrants the same full
          // logout as everywhere else.
          commonStore.reset();
          modalService.dismissAll();
          router.navigate([RoutesConstant.Login]);
        }
        throw error;
      }),
    );
    // return next(req);
  }

  const token = commonStore.loginData()?.token || null;

  // Detect browser-level storage clears: the in-memory store still holds the token,
  // but storage has been wiped. Force a logout before the request goes out so the
  // user cannot keep acting on a session that no longer exists.
  if (token && !hasStoredSession()) {
    performLogout(commonStore, logoutStore, modalService, router);
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: "Session storage cleared",
        }),
    );
  }

  const authReq = token
    ? req.clone({
        setHeaders: {
          Authorization: `Token ${token}`,
        },
      })
    : req;

  // return next(authReq);
  return next(authReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const body: any = event.body;
        if (body?.error?.toLowerCase() === "token not found") {
          console.error("Unauthorized");
          performLogout(
            commonStore,
            logoutStore,
            modalService,
            router,
            body?.redirection_url,
          );
          alertService.alertSuccess("Success!", "Successfully logged out");
        }
      }
    }),
  );
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const commonStore = inject(CommonSignalStore);
  const router = inject(Router);
  const logoutStore = inject(LogoutStore);
  const alertService = inject(AlertService);
  const modalService = inject(NgbModal);

  // twoFA and resend OTP errors are handled directly in the component.
  // The reset-password verify GET is handled by the create-password component's
  // resetLinkExpired template — a toast on top would be duplicate feedback.
  // Agent transfer GET returns 404 when no agent is configured (expected empty state).
  // SSO login errors are shown on the auth wrapper's SSO screen.
  const isSsoLogin = req.url.includes(API.SSO_LOGIN);
  const skipErrorAlert =
    isSsoLogin ||
    req.url.includes("converse/twofa") ||
    req.url.includes("converse/resend_otp") ||
    (req.method === "GET" &&
      req.url.includes("converse/customer/resetPassword")) ||
    (req.method === "GET" && /\/bot\/[^/]+\/agent$/.test(req.url));

  return next(req).pipe(
    catchError((error) => {
      const errorMessage = (error?.error?.error || "").toLowerCase();
      const redirectionUrl = error?.error?.redirection_url;

      // If backend says customer not found during any validation/API call, force logout
      if (!isSsoLogin && errorMessage.includes("customer not found")) {
        performLogout(
          commonStore,
          logoutStore,
          modalService,
          router,
          redirectionUrl,
        );
        return throwError(() => error);
      }

      switch (error?.status) {
        case 400:
          if (!skipErrorAlert)
            alertService.alertDanger("Failed!", error?.error?.error || "");
          break;
        case 401:
          // Redirect to login
          if (!skipErrorAlert)
            alertService.alertDanger("Failed!", error?.error?.error || "");

          // Expired SSO session: send the user back to their portal
          if (commonStore.isSso() && redirectionUrl) {
            performLogout(
              commonStore,
              logoutStore,
              modalService,
              router,
              redirectionUrl,
            );
          }
          break;
        case 403:
          // Access denied
          if (!skipErrorAlert)
            alertService.alertDanger("Failed!", error?.error?.error || "");
          break;
        case 404:
          // Not found
          if (!skipErrorAlert)
            alertService.alertDanger("Failed!", error?.error?.error || "");

          if (
            !skipErrorAlert &&
            error?.error?.error?.toLowerCase() === "token not found"
          ) {
            performLogout(
              commonStore,
              logoutStore,
              modalService,
              router,
              redirectionUrl,
            );
          }
          break;
        case 500:
          // Internal server error
          if (!isSsoLogin)
            alertService.alertDanger("Failed!", error?.error?.error || "");
          break;
      }
      return throwError(() => error);
    }),
  );
};
